import AsyncStorage from '@react-native-async-storage/async-storage';
import { getCurrentOrgID } from '@/data/organization';
import { filterRecentsForPlan, getLatestEntitlements } from '@/utils/organizationEntitlements';

const RECENTS_KEY = '@reach_recently_visited';
const MAX_RECENTS = 100;

// Module mapping from URL patterns to module names
const MODULE_MAPPING = {
  '/people': 'People',
  '/events': 'Events',
  '/tasks': 'Tasks',
  '/roles': 'Roles',
  '/training': 'Training',
  '/automations': 'Automations',
  '/paths': 'Automations', // legacy route — same module
  '/follow-up': 'Follow Up',
  '/smallgroups': 'Small Groups',
  '/biblestudies': 'Bible Studies',
  '/connect/conversations': 'Connect',
  '/connect/boards': 'Connect',
  '/forms': 'Forms',
};

// Get module name from URL
export const getModuleFromUrl = (url) => {
  for (const [pattern, moduleName] of Object.entries(MODULE_MAPPING)) {
    if (url?.startsWith(pattern)) {
      return moduleName;
    }
  }
  return 'Other';
};

// Save a resource to recently visited
export const saveToRecents = async (resource) => {
  try {
    const organizationID = await getCurrentOrgID();
    const recents = await getRecents();

    // Remove if already exists (to move to top)
    const filtered = recents.filter(r =>
      !(r.url === resource.url && r.id === resource.id && r.organizationID === organizationID)
    );

    // Add new item at the beginning
    const newRecents = [
      {
        ...resource,
        visitedAt: new Date().toISOString(),
        module: getModuleFromUrl(resource.url),
        organizationID,
      },
      ...filtered
    ].slice(0, MAX_RECENTS);

    await AsyncStorage.setItem(RECENTS_KEY, JSON.stringify(newRecents));
  } catch (error) {
    console.error('Error saving to recents:', error);
  }
};

// Get all recently visited resources
export const getRecents = async () => {
  try {
    const organizationID = await getCurrentOrgID();
    const data = await AsyncStorage.getItem(RECENTS_KEY);
    const allRecents = data ? JSON.parse(data) : [];

    // Filter by current organization, then by plan — a Free org must not
    // surface paid-module deep links. Storage keeps the full list (re-upgrade
    // restores it); only the returned view is sanitized.
    return filterRecentsForPlan(
      allRecents.filter(r => r.organizationID === organizationID),
      getLatestEntitlements()
    );
  } catch (error) {
    console.error('Error getting recents:', error);
    return [];
  }
};

// Remove a resource from recents
export const removeFromRecents = async (id, url) => {
  try {
    const organizationID = await getCurrentOrgID();
    const data = await AsyncStorage.getItem(RECENTS_KEY);
    const allRecents = data ? JSON.parse(data) : [];

    // Remove item matching id, url, and organizationID
    const filtered = allRecents.filter(r => !(r.id === id && r.url === url && r.organizationID === organizationID));
    await AsyncStorage.setItem(RECENTS_KEY, JSON.stringify(filtered));
  } catch (error) {
    console.error('Error removing from recents:', error);
  }
};

// Clear all recents for current organization
export const clearRecents = async () => {
  try {
    const organizationID = await getCurrentOrgID();
    const data = await AsyncStorage.getItem(RECENTS_KEY);
    const allRecents = data ? JSON.parse(data) : [];

    // Remove all items for current organization
    const filtered = allRecents.filter(r => r.organizationID !== organizationID);
    await AsyncStorage.setItem(RECENTS_KEY, JSON.stringify(filtered));
  } catch (error) {
    console.error('Error clearing recents:', error);
  }
};
